'use client';

import type React from 'react';
import { useTranslations } from 'next-intl';
import type { LucideIcon as LucideIconType } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { TotalMonthlyPriceIndicator } from '@/src/components/features/listings/TotalMonthlyPrice';
import { getListingIconType, ICON_FOR_LISTING_TYPE } from '@/src/lib/utils/listingPropertyType';
import { housematesPluralCount } from '@/src/lib/utils/listingUtils';
import { useListingFormatters } from '@/src/lib/utils/useListingFormatters';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import {
  feesApply,
  homeDetailsArea,
  homeDetailValue,
  identityAddress,
  identityIcon,
  identityLabel,
  identityLabelGroup,
  identityRow,
  priceRow,
  priceValue,
  totalPriceBadge,
  totalPriceCheckIcon,
  totalPriceLabel,
} from './listingCard.styles';

type HdpT = ReturnType<typeof useTranslations<'hdp'>>;
type ListingT = ReturnType<typeof useTranslations<'listing'>>;

/**
 * Splits a formatted spec phrase into its numeric value and unit label so the
 * value renders bold and the label normal-weight (e.g. "3" + "beds"). The label
 * is the trailing run of alphabetic words; everything before it is the value.
 * Phrases that are all letters (e.g. "Studio") have no separate label.
 */
function splitDetail(phrase: string): { value: string; label: string } {
  const tokens = phrase.trim().split(/\s+/);
  const labelTokens: Array<string> = [];
  while (tokens.length > 1 && /^[\p{L}.]+$/u.test(tokens[tokens.length - 1] ?? '')) {
    labelTokens.unshift(tokens.pop() as string);
  }
  return { value: tokens.join(' '), label: labelTokens.join(' ') };
}

function formatAddress(listing: SummaryListingDetails): string {
  const addr = listing.address;
  if (!addr) return '';
  const parts: Array<string> = [];
  if (addr.street && !addr.hideStreet) parts.push(addr.street);
  if (addr.city) parts.push(addr.city);
  if (addr.state) parts.push(addr.state);
  return parts.join(', ');
}

function formatCityStateZip(listing: SummaryListingDetails): string {
  const addr = listing.address;
  if (!addr) return '';
  const parts: Array<string> = [];
  if (addr.city) parts.push(addr.city);
  if (addr.state) parts.push(addr.state);
  const base = parts.join(', ');
  return addr.zip ? `${base} ${addr.zip}` : base;
}

/**
 * The card's identity line: a property-type icon (with an accessible title so
 * the type is exposed on hover and to screen readers) paired with the most
 * meaningful label for that type — building name for multi-unit properties, the
 * street address for a house, etc. The type is conveyed by the icon rather than
 * a separate text label.
 */
function getListingIdentity(
  listing: SummaryListingDetails,
  t: HdpT,
  tListing: ListingT,
): { Icon: LucideIconType; iconTitle: string; label: string } {
  const address = formatAddress(listing);
  const street = listing.address?.street && !listing.address?.hideStreet ? listing.address.street : address;
  const buildingName = listing.title || address;

  const iconType = getListingIconType(listing.propertyType, listing.listingType);
  const Icon = ICON_FOR_LISTING_TYPE[iconType];
  const iconTitle =
    iconType === 'building'
      ? t('apartmentBuilding')
      : iconType === 'condo'
        ? tListing('condo')
        : iconType === 'house'
          ? tListing('house')
          : tListing('roomForRent');

  return {
    Icon,
    iconTitle,
    label: iconType === 'house' ? street || buildingName : buildingName,
  };
}

/** The price, home details and identity text that the list card and the feed card both show. */
export function useListingCardSummary(listing: SummaryListingDetails) {
  const t = useTranslations('hdp');
  const tListing = useTranslations('listing');
  const { formatPrice, formatBedrooms, formatBathrooms, formatSqft } = useListingFormatters();

  const price = formatPrice(listing);
  const hasDollarPrice = price.startsWith('$');
  const unitCount = listing.modelTypeUnitCount ?? 0;
  const roomForRent = listing.listingType === 'room' ? (listing.roomForRent ?? listing.roomForRentInfo) : undefined;
  const roomSqft = roomForRent?.roomSqFt ? t('sqftRoom', { value: roomForRent.roomSqFt }) : null;
  const roommatesCount = roomForRent?.roommatesCount;

  return {
    priceDisplay: hasDollarPrice ? `${price}${price.endsWith('+') ? ' ' : ''}${t('perMoSlash')}` : price,
    hasDollarPrice,
    // beds / baths / sqft, or the unit count for buildings. A room for rent shows
    // the room's size in place of the home's, plus the housemates, as the HDP does.
    // Each phrase is split so the value renders bold and the unit label normal-weight.
    homeDetails: [
      formatBedrooms(listing),
      unitCount > 0 ? t('unitsAvailableSep', { count: unitCount }) : formatBathrooms(listing),
      roomSqft ?? (unitCount > 0 ? null : formatSqft(listing)),
      roommatesCount ? t('housemates', { count: housematesPluralCount(roommatesCount), label: roommatesCount }) : null,
    ]
      .filter((phrase): phrase is string => Boolean(phrase))
      .map(splitDetail),
    identity: getListingIdentity(listing, t, tListing),
    cityStateZip: formatCityStateZip(listing),
  };
}

export type ListingCardSummary = ReturnType<typeof useListingCardSummary>;

type ListingCardPriceRowProps = {
  listing: SummaryListingDetails;
  summary: ListingCardSummary;
  onTriggerClick: (event: React.MouseEvent) => void;
};

/** Price with the total-price / fees indicator inline beside it. */
export function ListingCardPriceRow({ listing, summary, onTriggerClick }: ListingCardPriceRowProps) {
  return (
    <span className={priceRow}>
      <span className={priceValue}>{summary.priceDisplay}</span>
      {summary.hasDollarPrice && (
        <TotalMonthlyPriceIndicator
          listing={listing}
          testId="listing-card-total-price"
          feesTestId="listing-card-fees"
          onTriggerClick={onTriggerClick}
          classNames={{
            badge: totalPriceBadge,
            fees: feesApply,
            icon: totalPriceCheckIcon,
            label: totalPriceLabel,
          }}
        />
      )}
    </span>
  );
}

/** Beds/baths/sqft on one line, with a literal · separator. */
export function ListingCardHomeDetails({ summary }: { summary: ListingCardSummary }) {
  return (
    <span className={homeDetailsArea} data-testid="listing-card-home-details">
      {summary.homeDetails.map((detail, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          <span className={homeDetailValue}>{detail.value}</span>
          {detail.label && ` ${detail.label}`}
        </span>
      ))}
    </span>
  );
}

/** Property-type icon + building name / street address, then city/state/zip. */
export function ListingCardIdentityRow({ summary }: { summary: ListingCardSummary }) {
  const { identity, cityStateZip } = summary;

  return (
    <span className={identityRow}>
      <span className={identityLabelGroup}>
        <LucideIcon icon={identity.Icon} size="sm" className={identityIcon} title={identity.iconTitle} />
        {identity.label && <span className={identityLabel}>{identity.label}</span>}
      </span>
      {cityStateZip && <span className={identityAddress}>{cityStateZip}</span>}
    </span>
  );
}
